import type {Suggestion, Suggestions} from '@github-ui/inline-autocomplete/types'
import {useQuery} from '@github-ui/react-query'
import {GitPullRequestIcon} from '@primer/octicons-react'
import {useMemo} from 'react'

import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {createEmptyStateSuggestion} from './EmptyStateSuggestion'
import {PullRequestSuggestion} from './PullRequestSuggestion'
import {useDebouncedFilterQuery} from './use-debounced-filter-query'

export interface PullRequestsQuery {
  category: 'pulls'
  repository: string
  filter: string
}

export function usePullRequestsSuggestions(query: PullRequestsQuery | null) {
  const {service} = useChatManager()

  const debouncedQuery = useDebouncedFilterQuery(query)
  const isDebouncing = query !== debouncedQuery

  const pullRequestsQuery = useQuery({
    enabled: debouncedQuery !== null,
    queryKey: ['copilot-autocomplete-pulls', debouncedQuery?.repository, debouncedQuery?.filter],
    queryFn: async () => service.fetchAutocompletePullRequests(debouncedQuery!.repository, debouncedQuery!.filter),
    placeholderData: prev => prev,
  })

  const suggestions = useMemo<Suggestions | null>(() => {
    if (!debouncedQuery) return null

    if (pullRequestsQuery.isLoading) return 'loading'

    const items =
      pullRequestsQuery.data?.map<Suggestion>(pull => ({
        value: ReferenceMention.stringify({
          type: 'pull-request',
          repo: debouncedQuery.repository,
          id: pull.number.toString(),
        }),
        key: pull.number.toString(),
        render: props => (
          <PullRequestSuggestion
            {...props}
            pullRequest={pull}
            stale={pullRequestsQuery.isPlaceholderData || isDebouncing}
          />
        ),
      })) ?? null

    if (items && items.length === 0 && !pullRequestsQuery.isPlaceholderData && !isDebouncing) {
      return [createEmptyStateSuggestion('pulls', <GitPullRequestIcon />, 'No pull requests found')]
    }

    return items
  }, [
    pullRequestsQuery.data,
    pullRequestsQuery.isLoading,
    pullRequestsQuery.isPlaceholderData,
    debouncedQuery,
    isDebouncing,
  ])

  return {suggestions, stale: pullRequestsQuery.isPlaceholderData}
}
